// Opções compartilhadas entre o lançamento no Contas a Pagar e o ticket de
// Compra/Contratação. Vivem aqui para que as duas telas nunca se descolem —
// antes a lista de formas de pagamento existia só dentro do ModalNovaDespesa e
// o ticket pedia a mesma informação em texto livre ("PIX", "Pix", "pix à vista").

export const FORMAS_PAGAMENTO = ['PIX', 'Boleto', 'TED', 'Cartão', 'Dinheiro', 'Cheque'] as const;

export const OPCOES_FORMA_PAGAMENTO = FORMAS_PAGAMENTO.map(v => ({ label: v, value: v }));

export type CondicaoPagamento = 'avista' | 'parcelado' | 'recorrente';

// Mesmos três modos do POST /contas-pagar: 'parcelado' DIVIDE o valor em N,
// 'recorrente' REPETE o valor cheio N meses.
export const OPCOES_CONDICAO: Array<{ label: string; value: CondicaoPagamento; ajuda: string }> = [
  { label: 'À vista', value: 'avista',
    ajuda: 'Um pagamento só.' },
  { label: 'Parcelado', value: 'parcelado',
    ajuda: 'Divide o valor total em N parcelas.' },
  { label: 'Mensal', value: 'recorrente',
    ajuda: 'Repete o valor cheio todo mês (ex.: terceiro mensalista).' },
];

// Campos que outra tela pode semear no modal de Nova despesa.
export type DespesaInicial = Partial<{
  fornecedor_original: string;
  data_competencia: string;
  descricao: string;
  valor: string;
  categoria_dre: string;
  grupo_dre: string;
  centro_custo: string;
  modo: CondicaoPagamento;
  parcelas: number;
  data_vencimento: string;
  metodo_pagamento: string;
  observacoes: string;
  // Fornecedor escolhido no cadastro pelo pedido (quando houve escolha).
  fornecedor_id: string;
  fornecedor_cnpj: string;
  // Cronograma escolhido no pedido: uma data por parcela.
  vencimentos: string[];
}> & {
  // Pistas para o modal resolver sozinho — NÃO são campos do formulário e não
  // podem entrar no payload do POST (ver CHAVES_AUXILIARES).
  // Contrato do pedido: casa com o centro de custo por identidade.
  centro_custo_contrato_id?: string;
  // Número do contrato ("HY26066") — leitor de pedido ANTIGO, que só guardou o
  // rótulo. Segundo passo, nunca em paralelo com o id.
  centro_custo_dica?: string;
  // Contratos do pedido quando há mais de um: o modal avisa que falta ratear.
  centros_custo_pedido?: string[];
};

// Chaves de `DespesaInicial` que são pista, não campo do lançamento. Quem
// espalha o `inicial` no estado do formulário precisa tirar estas — chave
// desconhecida vira campo fantasma e viaja no payload.
export const CHAVES_AUXILIARES = [
  'centro_custo_contrato_id',
  'centro_custo_dica',
  'centros_custo_pedido',
];

// Acha o centro de custo do pedido. SEQUENCIAL, nunca em paralelo:
//   1. por identidade do contrato (o centro traz o contrato dele e a família,
//      então aditivo cai no centro do contrato pai — aditivo não tem centro
//      próprio: 0 dos 77 em 18/09/2026);
//   2. só se o passo 1 não achou nada, pelos dígitos do número do contrato
//      ("HY26066" → "P_26066") — é o leitor de pedido ANTIGO, aberto antes de
//      o ticket guardar o id do contrato.
// Empate nunca vira chute: 2+ candidatos devolve null e o campo fica vazio,
// porque centro errado joga o custo no projeto errado.
export function acharCentroDoPedido(
  centros: any[],
  alvo: { contratoId?: string; dica?: string },
): string | null {
  const lista = Array.isArray(centros) ? centros : [];
  if (alvo.contratoId) {
    const porId = lista.filter((cc: any) => cc.contrato_id === alvo.contratoId
      || (Array.isArray(cc.contrato_ids) && cc.contrato_ids.includes(alvo.contratoId)));
    if (porId.length === 1) return porId[0].codigo_canonical;
    if (porId.length > 1) return null;
  }
  const dig = String(alvo.dica || '').match(/(\d{4,6})/);
  if (!dig) return null;
  // Mesma régua de código de centro que o motor da DRE usa para atribuir gasto
  // (dreCalculo.js e fechamentoCC.js): P, separador opcional, 4 a 6 dígitos.
  const porDig = lista.filter((cc: any) => {
    const m = String(cc.codigo_canonical || '').match(/P[_ -]?(\d{4,6})/);
    return m && m[1] === dig[1];
  });
  return porDig.length === 1 ? porDig[0].codigo_canonical : null;
}

// Traduz o pedido de Compra/Contratação para o lançamento do Contas a Pagar.
// O que o ticket já perguntou não é redigitado; o que ele não sabe (conta de
// onde sai o dinheiro, fornecedor do cadastro) fica em branco pra pessoa
// completar antes de confirmar.
export function despesaDoTicket(
  t: { titulo: string; protocolo?: string; dados?: any },
): DespesaInicial {
  const d = t.dados || {};
  const parcelas = parseInt(d.parcelas, 10);
  const parcelado = d.condicao === 'parcelado' || d.condicao === 'recorrente';
  // "HY26066 — Título do contrato" → "HY26066". Só com UM contrato: o pedido
  // aceita vários, e semear o primeiro jogaria 100% do valor nele — o outro
  // contrato ficaria com o lucro bonito de graça. Com vários, a pessoa monta
  // o rateio à mão (campo vazio é mais conferido que campo preenchido errado).
  const contratos = Array.isArray(d.centros_custo) ? d.centros_custo : [];
  const ids = Array.isArray(d.contrato_ids) ? d.contrato_ids.filter(Boolean) : [];
  const numeroContrato = contratos.length === 1
    ? String(contratos[0] || '').split('—')[0].trim()
    : '';
  // O id só vale com UM contrato dos dois lados: rótulo e id nascem do mesmo
  // campo do formulário, e exigir os dois evita semear a partir de um `dados`
  // remontado torto.
  const contratoId = contratos.length === 1 && ids.length === 1 ? String(ids[0]) : '';
  return {
    fornecedor_original: d.fornecedor || undefined,
    descricao: t.titulo,
    valor: d.valor != null && d.valor !== '' ? String(d.valor) : undefined,
    data_competencia: d.data_compra || undefined,
    // Sem vencimento o lançamento nem é aceito pelo servidor; a data da
    // compra é o menos pior, e a pessoa corrige antes de confirmar.
    data_vencimento: d.data_vencimento || d.data_compra || undefined,
    categoria_dre: d.categoria_dre || undefined,
    modo: parcelado ? d.condicao : 'avista',
    // O servidor exige no mínimo 2 quando não é à vista.
    parcelas: parcelado ? (parcelas >= 2 ? parcelas : 2) : 1,
    metodo_pagamento: d.forma_pagamento || undefined,
    // Fornecedor: o id só é uma SUGESTÃO — o modal confere se ele ainda existe
    // na lista do cadastro antes de usar (pode ter sido desativado ou mesclado
    // entre a abertura do pedido e o lançamento).
    fornecedor_id: d.fornecedor_id || undefined,
    fornecedor_cnpj: d.fornecedor_cnpj || undefined,
    // Cronograma escolhido no pedido. Ausente = o servidor calcula como sempre.
    vencimentos: Array.isArray(d.vencimentos) && d.vencimentos.length
      ? d.vencimentos.map(String) : undefined,
    observacoes: [
      t.protocolo ? `Pedido ${t.protocolo}` : null,
      d.pagamento_dados ? `Pagamento: ${d.pagamento_dados}` : null,
      // CNPJ não tem coluna no lançamento — sem esta linha some na passagem.
      d.fornecedor_cnpj ? `CNPJ: ${d.fornecedor_cnpj}` : null,
      // Centro de custo fica vazio de propósito quando há mais de um contrato;
      // sem esta linha a pessoa não saberia que falta ratear.
      contratos.length > 1 ? `RATEAR entre: ${contratos.join(' + ')}` : null,
    ].filter(Boolean).join(' · '),
    centro_custo_contrato_id: contratoId || undefined,
    centro_custo_dica: numeroContrato || undefined,
    centros_custo_pedido: contratos.length > 1 ? contratos.map(String) : undefined,
  };
}

// Categorias mais prováveis por tipo de pedido, na ordem em que fazem sentido
// para quem abre o ticket — que é do campo e não conhece a lista contábil.
// São intersectadas com o GET /categorias antes de virar opção: categoria
// renomeada ou desativada some da lista em vez de gravar um nome fantasma.
// A tela oferece "ver todas" para o caso que não estiver aqui.
export const CATEGORIAS_SUGERIDAS: Record<string, string[]> = {
  contratacao: [
    'Remuneração de Autônomos',
    'Honorários Terceirizados',
    'Remuneração de Auxiliares e Freelancers',
    'Materiais Aplicados na Prestação de Serviços',
    'ARTs',
    'Serviços / Assinaturas',
    'Honorários Contábeis',
    'Manutenção Predial',
    'Medicina do Trabalho',
  ],
  compra: [
    'Materiais Aplicados na Prestação de Serviços',
    'Bens de Pequeno Valor',
    'Ferramentas / EPIs',
    'Materiais de Escritório',
    'Copa, Cozinha, Higiene e Limpeza',
    'Software / Licença de Uso',
    'Combustíveis',
    'Uniformes',
  ],
  aluguel: [
    'Aluguel de equipamentos',
    'Aluguel',
    'Diárias / Hospedagem',
    'Leasing - Veículos',
    'Serviços / Assinaturas',
  ],
};
